import { activeResourceKeys, config, ecorpResources } from '@command-center/admin-ecorp';
import type {
  AdaptedNavGroup,
  AppShellConfiguration,
  PageHeaderConfig,
} from '@command-center/ui-system';
import { Building2, ClipboardCheck, FileStack, FolderKanban, UsersRound } from 'lucide-react';

const icons = {
  projects: FolderKanban,
  clients: Building2,
  contacts: UsersRound,
  documents: FileStack,
};

const navigation: AdaptedNavGroup[] = [
  {
    label: 'Workspace',
    items: [
      ...activeResourceKeys.map((key) => ({
        id: key,
        to: `/${ecorpResources[key].routePath}`,
        label: ecorpResources[key].label,
        icon: icons[key as keyof typeof icons],
      })),
      {
        id: 'approvals',
        to: '/approvals',
        label: 'Approvals',
        icon: ClipboardCheck,
      },
    ],
  },
];

export const ecorpShellConfig: AppShellConfiguration & {
  quickAccess: [];
} = {
  appId: config.appId,
  branding: {
    name: config.branding.name,
    logoSrc: config.branding.logo,
    iconSrc: config.branding.iconSrc,
    shortLabel: 'ECORP',
    iconText: 'EC',
    colors: config.branding.colors,
  },
  navigation,
  organization: {
    mode: 'single',
    name: config.clientName,
  },
  commandPalette: {
    enabled: Boolean(config.featureFlags?.commandPalette),
    shortcutLabel: '⌘K',
    placeholder: 'Search or run a command',
  },
  assistant: {
    enabled: Boolean(config.alice?.enabled),
    name: config.alice?.assistantName ?? 'Alice',
    statusLabel: 'Online',
    storageKey: 'ecorp:alice',
  },
  quickAccess: [],
  footer: 'v0.1.0 · admin',
};

export function getEcorpRouteHeader(pathname: string): PageHeaderConfig {
  if (pathname.startsWith('/projects/')) {
    return {
      title: 'Project Workspace',
      subtitle: 'Everything about this project in one reliable workspace.',
    };
  }
  if (pathname.startsWith('/clients/')) {
    return { title: 'Client', subtitle: 'Client details, contacts, and project history.' };
  }
  if (pathname.startsWith('/contacts/')) {
    return { title: 'Contact', subtitle: 'Contact details and client relationships.' };
  }
  if (pathname.startsWith('/documents/')) {
    return {
      title: 'Document',
      subtitle: 'Document details, extraction state, and review controls.',
    };
  }
  if (pathname === '/approvals') {
    return {
      title: 'Approvals',
      subtitle: 'Review extracted information before it updates a project.',
    };
  }
  if (pathname === '/activity') {
    return {
      title: 'Activity',
      subtitle: 'Recent workspace events across projects and documents.',
    };
  }
  if (pathname === '/alice-framework') {
    return {
      title: 'Alice Framework',
      subtitle: 'Configuration, tools, and connection health for Alice.',
    };
  }
  if (pathname === '/settings') {
    return {
      title: 'Workspace Settings',
      subtitle: 'Read-only ECORP workspace configuration and feature status.',
    };
  }
  if (pathname === '/profile') {
    return { title: 'User Settings', subtitle: 'Your signed-in account and ECORP access context.' };
  }

  const segment = pathname.split('/')[1] || 'projects';
  const resource =
    activeResourceKeys
      .map((key) => ecorpResources[key])
      .find((candidate) => candidate.routePath === segment) ?? ecorpResources.projects;
  return { title: resource.label, subtitle: resource.description };
}
